import type { CompositeFilterDescriptor, FilterDescriptor } from '@progress/kendo-data-query';
import type { Nullable } from '@ricoh/ria.client.core/types/core/data.type';

import { FilterOperator, isCompositeFilterDescriptor } from '@progress/kendo-data-query';
import { getStringValue } from '@ricoh/ria.client.core/utils/data/object';
import { flatItemMapper } from '@ricoh/ria.client.core/utils/mapper/flat-item';

import type { TransformFilter } from './_filter.type';

const apiFilterOperator: Record<string, string> = {
  [FilterOperator.EqualTo]: '==',
  [FilterOperator.NotEqualTo]: '!=',

  [FilterOperator.Contains]: '@=',
  [FilterOperator.DoesNotContain]: '!@=',

  [FilterOperator.GreaterThan]: '>',
  [FilterOperator.GreaterThanOrEqual]: '>=',

  [FilterOperator.LessThan]: '<',
  [FilterOperator.LessThanOrEqual]: '<=',

  [FilterOperator.StartsWith]: '_=',
  [FilterOperator.DoesNotStartWith]: '!_=',

  [FilterOperator.EndsWith]: '_-=',
  [FilterOperator.DoesNotEndWith]: '!_-=',

  [FilterOperator.IsEmpty]: '==',
  [FilterOperator.IsNull]: '==',

  [FilterOperator.IsNotEmpty]: '==',
  [FilterOperator.IsNotNull]: '!=',
} satisfies Record<FilterOperator, string>;

/**
 * Create FilterParam.
 *
 * @param {Nullable<CompositeFilterDescriptor>} filter Composite filter descriptor.
 * @param {TransformFilter} transform Transform.
 *
 * @returns {string[]} FilterParam.
 */
export function createFilterParam(filter: Nullable<CompositeFilterDescriptor>, transform?: TransformFilter): string[] {
  const result: string[] = [];

  (function build(filters: Nullable<FilterDescriptor | CompositeFilterDescriptor>[]) {
    filters.forEach((item) => {
      if (!item) {
        return;
      }

      if (isCompositeFilterDescriptor(item)) {
        build(item.filters);
        return;
      }

      const transformFilterDescriptors = transform ? transform(item) : [item];
      transformFilterDescriptors.forEach((transformFilterDescriptor) => {
        if (!transformFilterDescriptor.field) {
          return;
        }

        const filterField = getStringValue(transformFilterDescriptor.field);
        const operator = getStringValue(transformFilterDescriptor.operator);
        const filterOperator = apiFilterOperator[operator];
        if (!filterOperator) {
          return;
        }

        if (
          operator === FilterOperator.IsEmpty ||
          operator === FilterOperator.IsNull ||
          operator === FilterOperator.IsNotEmpty ||
          operator === FilterOperator.IsNotNull
        ) {
          result.push(`${filterField}${filterOperator}`);
        } else {
          result.push(`${filterField}${filterOperator}${transformFilterDescriptor.value}`);
        }
      });
    });
  })([filter]);

  return result;
}

/**
 * Create Filter.
 *
 * @param {string[]} filterParams Filter params.
 *
 * @returns {Nullable<CompositeFilterDescriptor>} Filter.
 */
export function createFilter(filterParams: string[]): Nullable<CompositeFilterDescriptor> {
  if (!filterParams?.length) {
    return null;
  }

  const filters = filterParams.reduce<FilterDescriptor[]>((prev, param) => {
    flatItemMapper.prepare(param).forEach((item) => {
      const apiFilterOperatorEntry = Object.entries(apiFilterOperator).find(([_operator, token]) => item.includes(token));
      if (!apiFilterOperatorEntry) {
        return;
      }

      const [field, value] = item.split(apiFilterOperatorEntry[1]);
      if (!field) {
        return;
      }

      prev.push({
        field,
        operator: apiFilterOperatorEntry[0],
        value,
      });
    });

    return prev;
  }, []);

  return {
    logic: 'and',
    filters,
  };
}
